import { useEffect, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel,
} from "@/components/ui/select";
import { Volume2, Loader2, Play, Square, Sparkles, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { loadVoices, isTTSSupported, previewVoice, cancelSpeech, rateFor } from "@/lib/tts";
import { filterEnglishVoices, voiceLabel, qualityTier, shouldOfferEnhancedVoiceHint, isIOSLikeDevice } from "@/lib/voiceFilter";

const DELAY_OPTIONS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
const SPEED_OPTIONS = [
  { value: "slow", label: "Slow" },
  { value: "normal", label: "Normal" },
  { value: "fast", label: "Fast" },
];
const FLOW_OPTIONS = [
  { value: "manual_next", label: "Wait for me to press Next" },
  { value: "continuous", label: "Read continuously" },
];

export default function AudioSettingsDialog({ open, onOpenChange, settings, onSaved }) {
  const [form, setForm] = useState(settings);
  const [voices, setVoices] = useState([]);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const previewRunIdRef = useRef(0);
  const supported = isTTSSupported();

  useEffect(() => { setForm(settings); }, [settings, open]);
  useEffect(() => {
    if (!open || !supported) return;
    // Show ONLY high-quality English voices — drops the dozens of irrelevant
    // system voices most mobiles enumerate (compact, novelty, espeak, etc.).
    loadVoices().then((vs) => setVoices(filterEnglishVoices(vs || [])));
  }, [open, supported]);

  // Stop any preview when the dialog closes — otherwise the voice keeps
  // talking into the void after the user clicks Cancel/Save.
  useEffect(() => {
    if (!open && previewing) {
      cancelSpeech();
      setPreviewing(false);
    }
    return () => { if (previewing) cancelSpeech(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onPreview = async () => {
    if (previewing) {
      cancelSpeech();
      setPreviewing(false);
      return;
    }
    const sel = form.selected_voice && form.selected_voice !== "default"
      ? voices.find((v) => v.name === form.selected_voice) || null
      : null;
    const runId = ++previewRunIdRef.current;
    setPreviewing(true);
    try {
      await previewVoice(sel, { rate: rateFor(form.reading_speed || "normal") });
    } catch (e) {
      toast.error(e?.message || "Could not play preview");
    } finally {
      // Only clear if this is the most recent preview run (avoid race with
      // the user clicking Stop+Play quickly which would already have reset it).
      if (previewRunIdRef.current === runId) setPreviewing(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        read_aloud_enabled: !!form.read_aloud_enabled,
        reading_flow: form.reading_flow || "manual_next",
        delay_before_answer_seconds: Number(form.delay_before_answer_seconds ?? 5),
        delay_before_next_question_seconds: Number(form.delay_before_next_question_seconds ?? 5),
        reading_speed: form.reading_speed || "normal",
        selected_voice: form.selected_voice || "default",
      };
      const r = await api.put("/student/audio-settings", payload);
      toast.success("Audio settings saved");
      onSaved?.(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="audio-settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Volume2 className="w-5 h-5 text-indigo-600" /> Audio Study Settings
          </DialogTitle>
          <DialogDescription>Customize how questions and answers are read aloud in Review Mode.</DialogDescription>
        </DialogHeader>

        {!supported && (
          <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-3" data-testid="audio-unsupported">
            Read Aloud is not supported on this device or browser. Settings can still be saved for use on another device.
          </div>
        )}

        <div className="space-y-5 py-2">
          <div className="flex items-center justify-between gap-3">
            <div>
              <Label htmlFor="enable-toggle" className="text-sm font-semibold">Enable Read Aloud Mode</Label>
              <p className="text-[11px] text-slate-500 mt-0.5">Auto-read questions when Review Mode starts</p>
            </div>
            <Switch
              id="enable-toggle"
              checked={!!form.read_aloud_enabled}
              onCheckedChange={(v) => update("read_aloud_enabled", v)}
              data-testid="audio-enable-switch"
            />
          </div>

          <div>
            <Label className="text-sm font-semibold">Reading Flow</Label>
            <Select value={form.reading_flow} onValueChange={(v) => update("reading_flow", v)}>
              <SelectTrigger className="mt-1" data-testid="audio-flow-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FLOW_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-sm font-semibold">Delay before reading the answer</Label>
            <Select
              value={String(form.delay_before_answer_seconds)}
              onValueChange={(v) => update("delay_before_answer_seconds", Number(v))}
            >
              <SelectTrigger className="mt-1" data-testid="audio-delay-answer-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DELAY_OPTIONS.map((d) => <SelectItem key={d} value={String(d)}>{d} seconds</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-sm font-semibold">Delay before next question</Label>
            <Select
              value={String(form.delay_before_next_question_seconds)}
              onValueChange={(v) => update("delay_before_next_question_seconds", Number(v))}
              disabled={form.reading_flow !== "continuous"}
            >
              <SelectTrigger className="mt-1" data-testid="audio-delay-next-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DELAY_OPTIONS.map((d) => <SelectItem key={d} value={String(d)}>{d} seconds</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-slate-500 mt-1">Used only when "Read continuously" is selected.</p>
          </div>

          <div>
            <Label className="text-sm font-semibold">Reading Speed</Label>
            <Select value={form.reading_speed} onValueChange={(v) => update("reading_speed", v)}>
              <SelectTrigger className="mt-1" data-testid="audio-speed-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SPEED_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {supported && voices.length > 0 && (() => {
            const selectedVoiceObj = form.selected_voice && form.selected_voice !== "default"
              ? voices.find((v) => v.name === form.selected_voice) || null
              : voices[0] || null;
            const showEnhancedHint = shouldOfferEnhancedVoiceHint(selectedVoiceObj);
            // Group voices by tier for a clearer picker.
            const groups = { Premium: [], High: [], Good: [], Standard: [], Basic: [] };
            for (const v of voices) {
              const t = qualityTier(v) || "Standard";
              (groups[t] || groups.Standard).push(v);
            }
            const groupOrder = ["Premium", "High", "Good", "Standard", "Basic"];
            return (
              <div>
                <Label className="text-sm font-semibold">Voice</Label>
                <div className="flex items-stretch gap-2 mt-1">
                  <div className="flex-1 min-w-0">
                    <Select value={form.selected_voice || "default"} onValueChange={(v) => update("selected_voice", v)}>
                      <SelectTrigger data-testid="audio-voice-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        <SelectItem value="default">
                          <span className="inline-flex items-center gap-1.5">
                            <Sparkles className="w-3 h-3 text-amber-500" />
                            Auto — best on this device
                          </span>
                        </SelectItem>
                        {groupOrder.map((tier) => {
                          const list = groups[tier] || [];
                          if (list.length === 0) return null;
                          return (
                            <SelectGroup key={tier}>
                              <SelectLabel className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                                {tier}
                              </SelectLabel>
                              {list.map((v) => (
                                <SelectItem key={v.name} value={v.name}>
                                  {voiceLabel(v)}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    type="button"
                    variant={previewing ? "destructive" : "secondary"}
                    size="sm"
                    onClick={onPreview}
                    className="shrink-0 h-10 px-3"
                    data-testid="audio-voice-preview-btn"
                    title={previewing ? "Stop preview" : "Preview voice"}
                  >
                    {previewing ? <Square className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                    <span className="ml-1.5 text-xs font-semibold">{previewing ? "Stop" : "Preview"}</span>
                  </Button>
                </div>
                <p className="text-[11px] text-slate-500 mt-1">
                  Grouped by quality tier — Premium voices are the most natural on your device.
                </p>

                {showEnhancedHint && (
                  <div
                    className="mt-3 rounded-md border border-indigo-200 bg-indigo-50/70 dark:bg-indigo-900/20 dark:border-indigo-800 p-3 text-xs text-indigo-900 dark:text-indigo-100 space-y-1.5"
                    data-testid="ios-enhanced-voice-hint"
                  >
                    <div className="flex items-center gap-1.5 font-semibold">
                      <Sparkles className="w-3.5 h-3.5" />
                      Make it sound more human on your iPhone
                    </div>
                    <p className="leading-snug">
                      Your device is using a legacy Siri voice. Download the free
                      "Enhanced" or "Premium" voice for a much more natural read-aloud:
                    </p>
                    <ol className="list-decimal ml-4 space-y-0.5">
                      <li>Open <span className="font-semibold">Settings</span> on your iPhone.</li>
                      <li>Tap <span className="font-semibold">Accessibility → Spoken Content → Voices → English</span>.</li>
                      <li>Pick <span className="font-semibold">Ava</span>, <span className="font-semibold">Zoe</span>, or <span className="font-semibold">Samantha</span> and tap the download <ExternalLink className="w-3 h-3 inline align-middle" /> next to <span className="font-semibold">(Premium)</span>.</li>
                      <li>Come back here and pick it from the Voice list above.</li>
                    </ol>
                  </div>
                )}

                {!showEnhancedHint && isIOSLikeDevice() && selectedVoiceObj && (
                  <p className="mt-2 text-[11px] text-emerald-700 dark:text-emerald-300 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> High-quality Siri voice detected — you're all set.
                  </p>
                )}
              </div>
            );
          })()}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="audio-settings-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-indigo-700 hover:bg-indigo-800" data-testid="audio-settings-save">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Save Settings
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
